// @ts-strict-ignore
import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Button,
  Combobox,
  ComboboxItem,
  Icon,
  Input,
  Tooltip,
} from '@node-space/ui-components'
import { ellipseMiddleText } from '@node-space/utils'
import useFilterMerchant from 'components/FilterBar/hooks/useFilterMerchant'
import useFilterSearch from 'components/FilterBar/hooks/useFilterSearch'
import { useMerchantsQuery } from 'hooks/queries'
import { PaymentMerchantDefault } from 'types/payments'
import { MerchantType } from 'types/types'
import { sanitizeUserInput, titleCase } from 'utils/utils'

const toComboboxItem = (merchant: MerchantType) => ({
  value: merchant?.merchantId,
  leftText:
    merchant?.displayName.length > 30 ? (
      <Tooltip
        triggerAsChild={false}
        bodyContent={merchant?.displayName}
      >{`${merchant?.displayName.substring(0, 25)}...`}</Tooltip>
    ) : (
      merchant?.displayName
    ),
  rightText: (
    <Tooltip triggerAsChild={false} bodyContent={merchant?.merchantId}>
      {ellipseMiddleText(merchant?.merchantId, 5, 5)}
    </Tooltip>
  ),
})

export const ChannelsFilterBar = () => {
  const { t } = useTranslation()
  const { data: { merchantList } = {}, isPending: isMerchantsLoading } = useMerchantsQuery()
  const { setSearch, search } = useFilterSearch()
  const { merchant, setMerchant } = useFilterMerchant()

  const merchantListOptions: ComboboxItem[] = [
    { value: PaymentMerchantDefault.ALL, leftText: titleCase(PaymentMerchantDefault.ALL) },
    ...(merchantList?.map(toComboboxItem) ?? []),
  ]

  const onMerchantSelected = merchant => {
    setMerchant(merchant)
  }

  const onSearchQueryChanged = e => {
    const { value } = e?.target ?? ''
    setSearch(sanitizeUserInput(value))
  }

  return (
    <Box flex gapX={12} gapY={16} className="mb-6" isFlexWrap>
      <Box className="w-full lg:w-auto lg:flex-1 lg:min-w-[300px]">
        <Input
          testId="filter-search-field"
          placeholderText={t('payments.channelPayments.searchChannelsPlaceholder')}
          onChange={onSearchQueryChanged}
          value={search || ''}
          searchable
          iconPosition="right"
          IconButton={
            search && (
              <Button
                variant="ghost"
                onClick={() => setSearch('')}
                iconElement={<Icon name="X" />}
              />
            )
          }
        />
      </Box>
      <Box className="block w-full lg:w-[360px]">
        <Combobox
          id="merchant"
          data-testid="filter-merchant-field"
          defaultValue={merchant || ''}
          onChange={onMerchantSelected}
          items={merchantListOptions}
          disabled={isMerchantsLoading}
        />
      </Box>

      {(search || merchant !== PaymentMerchantDefault.ALL) && !isMerchantsLoading && (
        <Box flex centerChildren>
          <Button
            variant="ghost"
            noPadding
            onClick={() => {
              setSearch('')
              setMerchant(PaymentMerchantDefault.ALL)
            }}
          >
            {t('clearFilters')}
          </Button>
        </Box>
      )}
    </Box>
  )
}
